import { useEffect, useRef } from 'react'

import { INLINE_LOGO_NAMES, INLINE_LOGOS } from './inlineLogos.js'

/**
 * Shared activity signal between the prompt textarea and the molecule.
 * Mutated imperatively (no React re-render) so the canvas rAF loop can
 * read the latest typing state every frame without churning the tree.
 */
export interface MoleculeActivity {
  /** performance.now() of the most recent keystroke. */
  lastTypeAt: number
  /** current textarea length, used to detect new characters. */
  length: number
  /** whether the textarea is focused. */
  focused: boolean
}

/**
 * Full logo pool (from public/images/trusted-logos/). Only ATOM_COUNT are
 * shown at once; atoms periodically crossfade-swap so every logo cycles
 * through over time. SVG preferred; cordova is png-only.
 */
const ALL_LOGOS = [
  'openai.svg',
  'anthropic.svg',
  'react.svg',
  'typescript.svg',
  'nextjs.svg',
  'vite.svg',
  'nodejs.svg',
  'python.svg',
  'tailwind.svg',
  'github.svg',
  'postgresql.svg',
  'stripe.svg',
  'aws.svg',
  'redis.svg',
  'mongodb.svg',
  'vue.svg',
  'svelte.svg',
  'go.svg',
  'deepseek.svg',
  'xai.svg',
  'slack.svg',
  'x.svg',
  'google.svg',
  'apple.svg',
  'microsoft.svg',
  'cloudflare.svg',
  'firebase.svg',
  'flutter.svg',
  'angular.svg',
  'redux.svg',
  'mysql.svg',
  'npm.svg',
  'discord.svg',
  'telegram.svg',
  'whatsapp.svg',
  'vscode.svg',
  'linux.svg',
  'electron.svg',
  'deepl.svg',
  'gitlab.svg',
  'sqlite.svg',
  'jest.svg',
  'dart.svg',
  'twilio.svg',
  'zoom.svg',
  'netlify.svg',
  'digitalocean.svg',
  'heroku.svg',
  'mixpanel.svg',
  'posthog.svg',
  'elasticsearch.svg',
  'meilisearch.svg',
  'rabbitmq.svg',
  'solid.svg',
  'jwt.svg',
  'axios.svg',
  'mapbox.svg',
  'openstreetmap.svg',
  'messenger.svg',
  'paypal.svg',
  'apple-pay.svg',
  'google-pay.svg',
  'sendgrid.svg',
  'mailgun.svg',
  'ses.svg',
  's3.svg',
  'render.svg',
  'ionic.svg',
  'capacitor.svg',
  'android.svg',
  'macos.svg',
  'windows.svg',
  'pwa.svg',
  'yarn.svg',
  'fitbit.svg',
  'oauth.svg',
  'biometric.svg',
]

/** how many atoms (logos) are visible on the sphere at once. */
const ATOM_COUNT = 26
/**
 * Viewport width at/above which the molecule sits to the *right* of the hero
 * (desktop layout); below it the molecule wraps around the hero (mobile).
 */
const DESKTOP_BP = 820
/** seconds between logo swaps (idle). */
const SWAP_EVERY = 1.5
/** seconds a crossfade swap takes. */
const SWAP_DUR = 0.55
/** seconds a freshly-loaded logo fades in over, so it never pops in blank. */
const APPEAR_DUR = 0.4

/** Accent palette per theme (matches the landing gradient). */
const PALETTE = {
  dark: ['#7ea5ff', '#e4a6b9', '#edc7a7', '#acc4fd'],
  light: ['#185eff', '#cc587b', '#dc9152', '#487dfb'],
}
const BOND_RGB = { dark: '126, 165, 255', light: '24, 94, 255' }

/**
 * Pure black/white marks that vanish on one theme, recoloured to the
 * theme foreground at load so they always contrast.
 */
const MONO = new Set([
  'apple.svg',
  'github.svg',
  'x.svg',
  'biometric.svg',
  'macos.svg',
  'openai.svg',
  'anthropic.svg',
  'xai.svg',
  'posthog.svg',
  'deepl.svg',
])
const MONO_TINT = { dark: '#e6e6e6', light: '#1a1a1a' }

/**
 * Logos that look (near-)identical share a visual group so two of them are
 * never shown at once, e.g. the X/xAI marks are both a stylised "X", and
 * Apple/iOS/macOS/Apple-Pay all lead with the Apple. Anything not listed is
 * its own group (keyed by filename).
 */
const GROUPS: Record<string, string> = {
  'x.svg': 'x',
  'xai.svg': 'x',
  'apple.svg': 'apple',
  'macos.svg': 'apple',
  'apple-pay.svg': 'apple',
  'google.svg': 'google',
  'google-pay.svg': 'google',
}

interface LogoImage {
  img: HTMLImageElement
  loaded: boolean
  /** kick off the network fetch (idempotent); deferred until the logo is shown */
  load: () => void
  /** 0→1 fade-in ramp, advanced once `loaded` flips true, so logos never pop in */
  appear: number
  /** natural width / height, preserves logo proportions when drawn */
  aspect: number
  /** theme-tinted silhouette for monochrome marks (drawn instead of img) */
  tinted?: HTMLCanvasElement
  /** visual-dedup group, never show two logos of the same group at once */
  group: string
}

interface Atom {
  /** base (unrotated) unit position */
  bx: number
  by: number
  bz: number
  /** rotated + projected per frame */
  sx: number
  sy: number
  scale: number
  z: number
  /** index into the loaded logo pool currently shown */
  idx: number
  /** target logo index during a crossfade swap, or -1 */
  swapTo: number
  /** crossfade progress 0..1 */
  swapT: number
}

interface Pulse {
  bond: number
  t: number
  speed: number
  color: string
}

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  maxLife: number
  size: number
  color: string
}

const FOCAL = 620
const GOLDEN = Math.PI * (3 - Math.sqrt(5))
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

interface Props {
  activity: React.MutableRefObject<MoleculeActivity>
  theme: 'light' | 'dark'
}

export default function MoleculeHero({ activity, theme }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const palette = PALETTE[theme]
    const bondRgb = BOND_RGB[theme]
    const monoTint = MONO_TINT[theme]
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    // The rAF animation is deferred until after first paint (see the loop start
    // at the bottom of the effect). Until `started` flips true, the molecule is
    // a still frame; logos that load before then set appear=1 and trigger a
    // batched still-frame repaint (`staticRaf`) so it fills in without the loop.
    let started = false
    let staticRaf = 0

    // --- logo pool (lazy) -----------------------------------------------
    // Only the logos that start on screen are fetched up front; reserve logos
    // load when a swap brings them in (see scheduleSwap). The pool has ~78
    // marks but at most N (26, every viewport) are ever visible, so eager
    // preloading meant ~50 wasted SVG requests on first paint.
    const images: LogoImage[] = ALL_LOGOS.map((name) => {
      const img = new Image()
      const meta: LogoImage = {
        img,
        loaded: false,
        load: () => {},
        appear: 0,
        aspect: 1,
        group: GROUPS[name] ?? name,
      }
      img.onload = () => {
        if (img.naturalWidth && img.naturalHeight)
          meta.aspect = img.naturalWidth / img.naturalHeight
        if (MONO.has(name)) {
          // recolour the silhouette to the theme foreground via source-in
          const S = 128
          const w = meta.aspect >= 1 ? S : S * meta.aspect
          const h = meta.aspect >= 1 ? S / meta.aspect : S
          const off = document.createElement('canvas')
          off.width = Math.max(1, Math.round(w))
          off.height = Math.max(1, Math.round(h))
          const octx = off.getContext('2d')
          if (octx) {
            octx.drawImage(img, 0, 0, off.width, off.height)
            octx.globalCompositeOperation = 'source-in'
            octx.fillStyle = monoTint
            octx.fillRect(0, 0, off.width, off.height)
            meta.tinted = off
          }
        }
        meta.loaded = true
        // Before the animation starts, the fade-in (which lives in the rAF
        // loop) isn't running — so show the logo immediately and repaint the
        // still frame, letting the molecule fill in as logos arrive.
        if (!started) {
          meta.appear = 1
          requestStaticRedraw()
        }
      }
      let requested = false
      meta.load = () => {
        if (requested) return
        requested = true
        // Initial-paint logos are bundled as inline data URLs (zero network
        // requests on first render); the long tail still loads over the network
        // when it swaps in. See scripts/gen-inline-logos.mjs.
        img.src = INLINE_LOGOS[name] ?? `/images/trusted-logos/${name}`
      }
      return meta
    })

    // The initial visible set must be drawn ONLY from the inlined logo pool so the
    // first frame issues zero /images/trusted-logos/* requests. The inline pool is
    // group-distinct and larger than the visible count (26 on every viewport), so a
    // group-deduped fill of N never overflows into the network-loaded long tail.
    // Shuffle within each partition so positions vary per load while the inlined,
    // most-popular set always paints first; the rest cycle in via swaps.
    const inlineNames = new Set<string>(INLINE_LOGO_NAMES)
    const shuffle = (arr: number[]) => {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = (Math.random() * (i + 1)) | 0
        ;[arr[i], arr[j]] = [arr[j], arr[i]]
      }
      return arr
    }
    const allIdx = images.map((_, i) => i)
    const inlineIdxs = shuffle(allIdx.filter((i) => inlineNames.has(ALL_LOGOS[i])))
    const restIdxs = shuffle(allIdx.filter((i) => !inlineNames.has(ALL_LOGOS[i])))
    const order = [...inlineIdxs, ...restIdxs]

    // The mobile sphere is larger and wraps the hero, and it used to RAISE the
    // count to 46 to read as a denser field — but every atom adds per-frame
    // draw + bond cost on the same main thread the prompt textarea types on,
    // and low-end phones visibly janked while typing. Capped at ATOM_COUNT
    // (26) on every viewport: perf bounds density, not the other way around.
    // (Fixed at init; re-inits on theme change.)
    const N = ATOM_COUNT
    // groups currently on-screen (or being swapped in), never two of one group
    const usedGroups = new Set<string>()
    // greedily fill the visible set from the front of `order`, skipping any
    // logo whose visual group is already taken
    const initialIdx: number[] = []
    for (const idx of order) {
      if (initialIdx.length >= N) break
      const g = images[idx].group
      if (usedGroups.has(g)) continue
      usedGroups.add(g)
      initialIdx.push(idx)
    }
    // fetch only the logos that start on screen; the rest load lazily on swap
    for (const idx of initialIdx) images[idx].load()

    // Progressively warm the reserve logos in the background so a swap always
    // has an already-fetched logo to crossfade to — no blank-while-fetching,
    // even on slow connections. Deferred + staggered so it never competes with
    // first paint or the initial visible set (preserves the lazy-load win that
    // avoids ~50 wasted SVG requests up front). load() is idempotent, so this
    // never double-fetches a logo a swap already pulled in.
    const initialSet = new Set(initialIdx)
    const reserve = order.filter((idx) => !initialSet.has(idx))
    let warmTimer: ReturnType<typeof setTimeout> | undefined
    let warmI = 0
    const warmNext = () => {
      if (warmI >= reserve.length) return
      images[reserve[warmI++]].load()
      warmTimer = setTimeout(warmNext, 150)
    }
    warmTimer = setTimeout(warmNext, 800)

    const atoms: Atom[] = []
    for (let i = 0; i < initialIdx.length; i++) {
      const y = 1 - (i / (N - 1)) * 2
      const r = Math.sqrt(Math.max(0, 1 - y * y))
      const theta = GOLDEN * i
      atoms.push({
        bx: Math.cos(theta) * r,
        by: y,
        bz: Math.sin(theta) * r,
        sx: 0,
        sy: 0,
        scale: 1,
        z: 0,
        idx: initialIdx[i],
        swapTo: -1,
        swapT: 0,
      })
    }

    // --- bonds: connect each atom to its nearest neighbours -------------
    const bondSet = new Set<string>()
    const bonds: [number, number][] = []
    const atomN = atoms.length
    for (let i = 0; i < atomN; i++) {
      const dists: { j: number; d: number }[] = []
      for (let j = 0; j < atomN; j++) {
        if (i === j) continue
        const dx = atoms[i].bx - atoms[j].bx
        const dy = atoms[i].by - atoms[j].by
        const dz = atoms[i].bz - atoms[j].bz
        dists.push({ j, d: dx * dx + dy * dy + dz * dz })
      }
      dists.sort((a, b) => a.d - b.d)
      for (let k = 0; k < 3 && k < dists.length; k++) {
        const j = dists[k].j
        const key = i < j ? `${i}-${j}` : `${j}-${i}`
        if (!bondSet.has(key)) {
          bondSet.add(key)
          bonds.push(i < j ? [i, j] : [j, i])
        }
      }
    }

    const pulses: Pulse[] = []
    const particles: Particle[] = []

    // --- sizing ----------------------------------------------------------
    let dpr = 1
    let cssW = 0
    let cssH = 0
    let cx = 0
    let cy = 0
    let R = 200
    // perspective focal length, must stay > R so no atom crosses behind the
    // camera (which would yield a negative scale → negative gradient radius →
    // a thrown error that kills the whole render loop, e.g. on wide tablets).
    let focal = FOCAL

    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      cssW = rect.width
      cssH = rect.height
      dpr = Math.min(2, window.devicePixelRatio || 1)
      canvas.width = Math.round(cssW * dpr)
      canvas.height = Math.round(cssH * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      // anchor vertically to the actual hero content (the tagline block),
      // not the oversized #sticky-top container.
      const tagline = document.getElementById('tagline')
      const form = document.querySelector('.landing-prompt-form')
      const heroMidY = tagline
        ? tagline.getBoundingClientRect().top +
          tagline.getBoundingClientRect().height / 2 -
          rect.top
        : cssH * 0.3

      if (cssW >= DESKTOP_BP) {
        // desktop / tablet, to the right of the hero text, overflowing the edge
        cx = cssW * 0.8
        cy = heroMidY - 20
        R = clamp(cssW * 0.2, 220, 340)
      } else {
        // mobile / tablet, wrap the whole hero: a large sphere centred on the
        // hero/textarea, bleeding past every edge (above the top, off L/R).
        const formCenterY = form
          ? form.getBoundingClientRect().top + form.getBoundingClientRect().height / 2 - rect.top
          : heroMidY
        cx = cssW * 0.5
        // centre on the hero (tagline), biased a touch up toward the brand so
        // the sphere wraps up past the top of the page as well as the textarea
        cy = Math.min(heroMidY, formCenterY) - 10
        R = Math.max(cssW * 0.88, 320)
      }

      // keep the focal length comfortably larger than the radius so every
      // atom stays in front of the camera (scale > 0 always)
      focal = Math.max(FOCAL, R * 1.7)

      // While the animation loop is deferred, a resize won't otherwise repaint,
      // so refresh the still frame at the new geometry.
      if (!started) requestStaticRedraw()
    }

    let ro: ResizeObserver | null = null
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(resize)
      ro.observe(canvas)
    }
    resize()

    // --- animation state -------------------------------------------------
    let ry = 0.4
    let rx = -0.25
    // continuous rotation on BOTH axes so the poles constantly drift and no
    // atom ever sits still on the rotation axis
    let spinY = 0.12 // angular velocity around Y (may go negative)
    let spinX = 0.07 // angular velocity around X (slower tumble)
    let dirY = 1 // resting Y direction
    let dirX = 1 // resting X direction
    let lastFocused = false
    let lastLen = 0
    let pulseTimer = 0
    let swapTimer = SWAP_EVERY
    let raf = 0
    let last = performance.now()
    // Loop runs only when `started` (post-paint) AND `visible` (on-screen).
    let running = false
    let visible = true

    const spawnPulse = (count: number) => {
      for (let i = 0; i < count; i++) {
        pulses.push({
          bond: (Math.random() * bonds.length) | 0,
          t: 0,
          speed: 0.6 + Math.random() * 0.9,
          color: palette[(Math.random() * palette.length) | 0],
        })
      }
      if (pulses.length > 60) pulses.splice(0, pulses.length - 60)
    }

    const spawnBurst = (ax: number, ay: number, color: string) => {
      for (let i = 0; i < 6; i++) {
        const a = Math.random() * Math.PI * 2
        const sp = 20 + Math.random() * 50
        particles.push({
          x: ax,
          y: ay,
          vx: Math.cos(a) * sp,
          vy: Math.sin(a) * sp,
          life: 0,
          maxLife: 0.6 + Math.random() * 0.7,
          size: 1 + Math.random() * 2,
          color,
        })
      }
      if (particles.length > 220) particles.splice(0, particles.length - 220)
    }

    // start a crossfade swap on a random idle atom → an unseen logo
    const swapRandomAtom = () => {
      // candidates = logos whose visual group is not currently on-screen.
      // Prefer ones already fetched so the crossfade starts instantly and never
      // flashes a blank atom; only fall back to an unfetched logo when nothing
      // is ready — and then the crossfade waits for it (see "advance active
      // swaps"), holding the atom's current logo until it arrives.
      const ready: number[] = []
      const unloaded: number[] = []
      for (let i = 0; i < images.length; i++) {
        if (usedGroups.has(images[i].group)) continue
        ;(images[i].loaded ? ready : unloaded).push(i)
      }
      const pool = ready.length ? ready : unloaded
      if (!pool.length) return
      // pick an atom not currently swapping
      const idle: number[] = []
      for (let i = 0; i < atoms.length; i++) if (atoms[i].swapTo < 0) idle.push(i)
      if (!idle.length) return
      const a = atoms[idle[(Math.random() * idle.length) | 0]]
      const newIdx = pool[(Math.random() * pool.length) | 0]
      images[newIdx].load() // idempotent; warms it if we fell back to unloaded
      usedGroups.add(images[newIdx].group)
      a.swapTo = newIdx
      a.swapT = 0
    }

    // draw a logo fitted within `size`, preserving aspect ratio
    const drawLogo = (m: LogoImage, x: number, y: number, size: number, alpha: number) => {
      const a = alpha * m.appear
      if (!m.loaded || a <= 0) return
      const dw = m.aspect >= 1 ? size : size * m.aspect
      const dh = m.aspect >= 1 ? size / m.aspect : size
      ctx.globalAlpha = a
      try {
        ctx.drawImage(m.tinted ?? m.img, x - dw / 2, y - dh / 2, dw, dh)
      } catch (_error) {
        /* safe noop: drawImage can throw if the image is partially decoded;
           the rAF loop retries every frame so skipping one draw is harmless */
      }
      ctx.globalAlpha = 1
    }

    const draw = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000)
      last = now

      // energy from typing / focus -------------------------------------
      const act = activity.current
      const sinceType = now - act.lastTypeAt
      const typeEnergy = clamp(1 - sinceType / 1300, 0, 1)
      const energy = Math.max(typeEnergy, act.focused ? 0.28 : 0)

      // new character → burst + pulses + a random spin impulse
      if (act.length !== lastLen) {
        const added = act.length > lastLen
        lastLen = act.length
        if (added) {
          spawnPulse(3)
          const a = atoms[(Math.random() * atoms.length) | 0]
          spawnBurst(a.sx || cx, a.sy || cy, palette[(Math.random() * palette.length) | 0])
          // nudge both rotation axes in random directions
          spinY += (Math.random() * 2 - 1) * (0.6 + energy)
          spinX += (Math.random() * 2 - 1) * (0.4 + energy * 0.6)
          if (Math.random() < 0.25) dirY = -dirY
          if (Math.random() < 0.25) dirX = -dirX
        }
      }

      // focus → kick + maybe reverse a resting direction
      if (act.focused && !lastFocused) {
        spinY += (Math.random() * 2 - 1) * 0.8
        spinX += (Math.random() * 2 - 1) * 0.6
        if (Math.random() < 0.5) dirY = -dirY
        if (Math.random() < 0.5) dirX = -dirX
      }
      lastFocused = act.focused

      // rotation: each axis eases toward its resting spin (plus the transient
      // impulses above, which decay back out naturally as the ease pulls in)
      const targetY = reduceMotion ? 0 : (0.12 + energy * 0.6) * dirY
      const targetX = reduceMotion ? 0 : (0.07 + energy * 0.3) * dirX
      spinY += (targetY - spinY) * Math.min(1, dt * 1.6)
      spinX += (targetX - spinX) * Math.min(1, dt * 1.6)
      ry += spinY * dt
      rx += spinX * dt

      // idle + active pulse trickle
      if (!reduceMotion) {
        pulseTimer -= dt
        const interval = 0.9 - energy * 0.75
        if (pulseTimer <= 0) {
          pulseTimer = interval
          spawnPulse(1 + ((energy * 2) | 0))
        }
        // periodic logo swaps (a touch faster while typing)
        swapTimer -= dt
        if (swapTimer <= 0) {
          swapTimer = SWAP_EVERY * (1 - energy * 0.5)
          swapRandomAtom()
        }
      }

      // fade each logo in once it finishes loading, so it never appears blank
      for (const m of images) {
        if (m.loaded && m.appear < 1) m.appear = Math.min(1, m.appear + dt / APPEAR_DUR)
      }

      // advance active swaps
      for (const a of atoms) {
        if (a.swapTo >= 0) {
          // Hold the current logo (swapT pinned at 0 → drawn at full alpha)
          // until the incoming one is fully fetched, so a slow network never
          // blanks the atom mid-swap. Once it's loaded the crossfade runs.
          if (!images[a.swapTo].loaded) continue
          a.swapT += dt / SWAP_DUR
          if (a.swapT >= 1) {
            usedGroups.delete(images[a.idx].group)
            a.idx = a.swapTo
            a.swapTo = -1
            a.swapT = 0
          }
        }
      }

      // --- project atoms ---------------------------------------------
      const cosY = Math.cos(ry)
      const sinY = Math.sin(ry)
      const cosX = Math.cos(rx)
      const sinX = Math.sin(rx)
      for (const a of atoms) {
        const x1 = a.bx * cosY + a.bz * sinY
        const z1 = -a.bx * sinY + a.bz * cosY
        const y1 = a.by
        const y2 = y1 * cosX - z1 * sinX
        const z2 = y1 * sinX + z1 * cosX
        const px = x1 * R
        const py = y2 * R
        const pz = z2 * R
        const scale = focal / (focal + pz)
        a.sx = cx + px * scale
        a.sy = cy + py * scale
        a.scale = scale
        a.z = pz
      }

      ctx.clearRect(0, 0, cssW, cssH)

      // --- centre glow -----------------------------------------------
      const glowR = R * 2
      const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, glowR)
      const glowA = 0.08 + energy * 0.16
      glow.addColorStop(0, `rgba(${bondRgb}, ${glowA})`)
      glow.addColorStop(1, `rgba(${bondRgb}, 0)`)
      ctx.fillStyle = glow
      ctx.fillRect(cx - glowR, cy - glowR, glowR * 2, glowR * 2)

      // --- bonds ------------------------------------------------------
      ctx.lineCap = 'round'
      for (const [i, j] of bonds) {
        const a = atoms[i]
        const b = atoms[j]
        const depth = (a.scale + b.scale) / 2
        const alpha = clamp((depth - 0.6) / 0.9, 0, 1) * (0.32 + energy * 0.32)
        ctx.strokeStyle = `rgba(${bondRgb}, ${alpha})`
        ctx.lineWidth = depth * (1.2 + energy * 0.8)
        ctx.beginPath()
        ctx.moveTo(a.sx, a.sy)
        ctx.lineTo(b.sx, b.sy)
        ctx.stroke()
      }

      // --- pulses along bonds ----------------------------------------
      for (let p = pulses.length - 1; p >= 0; p--) {
        const pulse = pulses[p]
        pulse.t += pulse.speed * dt
        if (pulse.t >= 1) {
          pulses.splice(p, 1)
          continue
        }
        const [i, j] = bonds[pulse.bond]
        const a = atoms[i]
        const b = atoms[j]
        const x = a.sx + (b.sx - a.sx) * pulse.t
        const y = a.sy + (b.sy - a.sy) * pulse.t
        const depth = a.scale + (b.scale - a.scale) * pulse.t
        const fade = Math.sin(pulse.t * Math.PI)
        const rad = 2.4 * depth
        const g = ctx.createRadialGradient(x, y, 0, x, y, rad * 3)
        g.addColorStop(0, pulse.color)
        g.addColorStop(1, 'transparent')
        ctx.globalAlpha = fade
        ctx.fillStyle = g
        ctx.beginPath()
        ctx.arc(x, y, rad * 3, 0, Math.PI * 2)
        ctx.fill()
        ctx.globalAlpha = 1
      }

      // --- ambient particles -----------------------------------------
      for (let p = particles.length - 1; p >= 0; p--) {
        const pt = particles[p]
        pt.life += dt
        if (pt.life >= pt.maxLife) {
          particles.splice(p, 1)
          continue
        }
        pt.x += pt.vx * dt
        pt.y += pt.vy * dt
        pt.vx *= 0.96
        pt.vy *= 0.96
        const fade = 1 - pt.life / pt.maxLife
        ctx.globalAlpha = fade * 0.8
        ctx.fillStyle = pt.color
        ctx.beginPath()
        ctx.arc(pt.x, pt.y, pt.size, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.globalAlpha = 1

      // --- atoms (back to front) -------------------------------------
      const sorted = atoms.slice().sort((a, b) => b.z - a.z)
      for (const a of sorted) {
        const size = 38 * a.scale
        const depthAlpha = clamp((a.scale - 0.68) / 0.95, 0.42, 1)

        // soft halo behind the logo
        const halo = ctx.createRadialGradient(a.sx, a.sy, 0, a.sx, a.sy, size)
        halo.addColorStop(0, `rgba(${bondRgb}, ${0.22 * depthAlpha})`)
        halo.addColorStop(1, `rgba(${bondRgb}, 0)`)
        ctx.fillStyle = halo
        ctx.beginPath()
        ctx.arc(a.sx, a.sy, size, 0, Math.PI * 2)
        ctx.fill()

        if (a.swapTo >= 0) {
          // crossfade between the old and new logo
          drawLogo(images[a.idx], a.sx, a.sy, size, depthAlpha * (1 - a.swapT))
          drawLogo(images[a.swapTo], a.sx, a.sy, size, depthAlpha * a.swapT)
        } else {
          drawLogo(images[a.idx], a.sx, a.sy, size, depthAlpha)
        }
      }

      if (running) raf = requestAnimationFrame(draw)
    }

    // Repaint the current state once, without advancing time (dt≈0 → no
    // rotation jump between sparse still repaints).
    function drawStatic(): void {
      last = performance.now()
      draw(last)
    }
    // Coalesce logo-load repaints into a single still frame while deferred.
    function requestStaticRedraw(): void {
      if (started || staticRaf) return
      staticRaf = requestAnimationFrame(() => {
        staticRaf = 0
        if (!started) drawStatic()
      })
    }
    const startLoop = (): void => {
      if (!started || !visible || running) return
      running = true
      last = performance.now()
      raf = requestAnimationFrame(draw)
    }
    const stopLoop = (): void => {
      if (!running) return
      running = false
      cancelAnimationFrame(raf)
    }
    const startAnimation = (): void => {
      if (started) return
      started = true
      if (staticRaf) {
        cancelAnimationFrame(staticRaf)
        staticRaf = 0
      }
      startLoop()
    }

    // pause when scrolled out of view --------------------------------
    let io: IntersectionObserver | null = null
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(
        ([entry]) => {
          visible = entry.isIntersecting
          if (visible) startLoop()
          else stopLoop()
        },
        { threshold: 0 },
      )
      io.observe(canvas)
    }

    // Paint one still frame now (so the molecule is there for FCP/LCP and fills
    // in as logos load), then defer the rAF loop until the page has loaded and
    // gone idle — the canvas is the biggest main-thread cost and must not steal
    // the thread during first paint (that made FCP/LCP swing 1s↔3.3s).
    drawStatic()
    type IdleWin = Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
      cancelIdleCallback?: (id: number) => void
    }
    const idleWin = window as IdleWin
    let startIdleId: number | undefined
    const scheduleStart = (): void => {
      const ric =
        idleWin.requestIdleCallback ??
        ((cb: () => void) => window.setTimeout(cb, 300) as unknown as number)
      startIdleId = ric(startAnimation, { timeout: 1500 })
    }
    if (document.readyState === 'complete') scheduleStart()
    else window.addEventListener('load', scheduleStart, { once: true })

    return () => {
      running = false
      started = true // block any pending logo-load static repaint after unmount
      cancelAnimationFrame(raf)
      if (staticRaf) cancelAnimationFrame(staticRaf)
      ro?.disconnect()
      io?.disconnect()
      window.removeEventListener('load', scheduleStart)
      if (startIdleId != null) {
        idleWin.cancelIdleCallback?.(startIdleId)
        clearTimeout(startIdleId)
      }
      if (warmTimer) clearTimeout(warmTimer)
    }
  }, [theme, activity])

  return <canvas ref={canvasRef} className="molecule-canvas" aria-hidden="true" />
}
